<template>
  <!-- 商机概况：商机阶段分布与金额预测 -->
  <div class="opportunity-overview">
    <list-page>
      <search-condition @search="reloadAll" :show-search="true" page-name="opportunityOverview">
        <template #default>
          <el-select v-model="query.currentStage" placeholder="全部" clearable>
            <template #prefix>当前阶段</template>
            <el-option v-for="s in stageList" :key="s.userCode" :label="s.dictName" :value="Number(s.userCode)"/>
          </el-select>
          <!-- 普通角色只看得到与自己相关的商机，按负责人再筛一次没有意义，直接不显示 -->
          <employee-select v-if="isSuperAdmin" v-model="query.ownerEmployeeId" placeholder="全部" @change="reloadAll">
            <template #prefix>负责人</template>
          </employee-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="reloadAll">刷新</page-button>
        </template>
      </search-condition>
      <list-table>
        <!-- KPI -->
        <div v-loading="loading" class="stat-grid">
          <div class="stat-card">
            <div class="label">商机总数</div>
            <div class="value">{{ overview.opportunityCount || 0 }}<span class="unit">个</span></div>
            <div class="sub">全部销售商机</div>
          </div>
          <div class="stat-card">
            <div class="label">商机总金额</div>
            <div class="value money">{{ money(overview.opportunityAmount) }}<span class="unit">元</span></div>
          </div>
          <div class="stat-card">
            <div class="label">加权预测金额</div>
            <div class="value money">{{ money(overview.predictAmount) }}<span class="unit">元</span></div>
            <div class="sub">按各商机成交概率加权</div>
          </div>
          <div class="stat-card">
            <div class="label">成交率</div>
            <div class="value">{{ (overview.dealRate || 0) }}<span class="unit">%</span></div>
            <div class="sub">成交阶段商机数 ÷ 商机总数</div>
          </div>
        </div>

        <!-- 阶段分布：数量 + 金额 -->
        <div v-loading="loading" class="grid-2">
          <div class="chart-card">
            <div class="section-title">商机阶段分布（数量）</div>
            <div ref="countChart" class="chart-div"></div>
          </div>
          <div class="chart-card">
            <div class="section-title">商机阶段金额（元）</div>
            <div ref="amountChart" class="chart-div"></div>
          </div>
        </div>

        <!--
          预警统计（分跟进预警 / 成交预警两组）。
          与上方 KPI 卡样式刻意区分：KPI 是白底大卡片展示「量」，预警是可点击的窄条，
          颜色按紧急程度区分、数字加粗，且只在有数据时才出现 —— 免得四个 0 占着一行。
        -->
        <div v-loading="loading" class="warn-panel">
          <div class="warn-row">
            <div class="warn-title">跟进预警</div>
            <div class="warn-tags">
            <span class="warn-tag warn-tag-warn" @click="openWarnDialog(1)">
              7天未联系 <b>{{ warnStat.followWarn7Count || 0 }}</b>
            </span>
              <span class="warn-tag warn-tag-danger" @click="openWarnDialog(2)">
              15天未联系 <b>{{ warnStat.followWarn15Count || 0 }}</b>
            </span>
            </div>
          </div>
          <div class="warn-row">
            <div class="warn-title">成交预警</div>
            <div class="warn-tags">
            <span class="warn-tag warn-tag-info" @click="openWarnDialog(3)">
              预计成交剩余7天 <b>{{ warnStat.dealWarn7Count || 0 }}</b>
            </span>
              <span class="warn-tag warn-tag-primary" @click="openWarnDialog(4)">
              预计成交剩余15天 <b>{{ warnStat.dealWarn15Count || 0 }}</b>
            </span>
            </div>
          </div>
        </div>

        <!-- 金额趋势 -->
        <div v-loading="loading" class="chart-card">
          <div class="section-title">商机金额趋势</div>
          <div ref="trendChart" class="chart-div"></div>
        </div>
      </list-table>
    </list-page>

    <!-- 预警商机列表：点某个预警卡片后弹出，带分页 -->
    <el-dialog v-model="showWarnDialog" :title="warnDialogTitle" dialog-width="3" :close-on-click-modal="false">
      <dialog-table>
        <list-page :show-shadow="false" :show-border="false">
          <list-table>
            <yq-table :data="warnList" :empty-text="'暂无预警商机'" height="100%">
              <yq-table-column label="商机编号" prop="opportunityNo" width="130rem"/>
              <yq-table-column label="商机名称" prop="opportunityTitle" min-width="200rem" :show-overflow-tooltip="true"/>
              <yq-table-column label="当前阶段" prop="currentStage" align="center" width="110rem">
                <template v-slot="{row}">
                  <el-tag size="small" :type="stageTagType(row.currentStage)">{{ stageName(row.currentStage) }}</el-tag>
                </template>
              </yq-table-column>
              <yq-table-column label="商机金额" prop="opportunityAmount" align="right" width="120rem">
                <template v-slot="{row}">{{ money(row.opportunityAmount) }}</template>
              </yq-table-column>
              <!-- 跟进预警看「多久没联系」，成交预警看「还有多少天到期」，两列常驻并标出对应那一项 -->
              <yq-table-column label="上次跟进" prop="prevFollowDate" width="120rem">
                <template v-slot="{row}">
                  <span :class="{'warn-cell': isFollowWarn}">{{ dateOnly(row.prevFollowDate) }}</span>
                </template>
              </yq-table-column>
              <yq-table-column label="预计成交" prop="expectDealDate" width="120rem">
                <template v-slot="{row}">
                  <span :class="{'warn-cell': !isFollowWarn}">{{ dateOnly(row.expectDealDate) }}</span>
                </template>
              </yq-table-column>
              <yq-table-column label="负责人" prop="ownerEmployeeName" width="100rem"/>
            </yq-table>
          </list-table>
          <form-page :show-shadow="false" :show-border="false"
                     v-model:page-number="warnSearchInfo.pageNumber"
                     v-model:page-size="warnSearchInfo.pageSize"
                     :total-count="warnSearchInfo.totalCount" @onLoad="queryWarnList">
            <page-button @click="showWarnDialog = false">关闭</page-button>
          </form-page>
        </list-page>
      </dialog-table>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import DialogTable from "@/components/h5/layout/DialogTable.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import YqTable from "@/components/h5/table/YqTable.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import opportunityOverviewApi from "@/api/dz-crm/partner/opportunityOverviewApi";
import {DICT_OPPORTUNITY_STAGE, fillStageRows, stageTagType, dictName} from "@/views/h5/crm/crmDictionary";
import commonApi from "@/api/commonApi";
import Util from "@/util/util";
import {useRoute, useRouter} from "vue-router";

/**
 * 预警档位。与后端 OpportunityQueryVo.warnType 一一对应：
 * 1/2 是跟进预警（未联系），3/4 是成交预警（预计成交临近）。
 * 四档互斥，同一个商机只会落在其中一档。
 */
const WARN_TYPE = {FOLLOW_7: 1, FOLLOW_15: 2, DEAL_7: 3, DEAL_15: 4};

const WARN_TYPE_TITLE = {
  [WARN_TYPE.FOLLOW_7]: "7天未联系",
  [WARN_TYPE.FOLLOW_15]: "15天未联系",
  [WARN_TYPE.DEAL_7]: "预计成交剩余7天",
  [WARN_TYPE.DEAL_15]: "预计成交剩余15天",
};

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "OpportunityOverview",
  components: {
    ListPage, SearchCondition, PageButton, EmployeeSelect,
    ListTable, FormPage, DialogTable, YqTable, YqTableColumn,
  },
  data() {
    return {
      loading: false,
      query: {currentStage: "", ownerEmployeeId: ""},
      overview: {},
      stageList: [],
      dictReady: false,
      dataReady: false,
      countChart: null,
      amountChart: null,
      trendChart: null,
      // 角色分类，1超级管理员 2普通角色。普通角色只看得到与自己相关的商机
      roleType: "",
      // 四档预警数量
      warnStat: {},
      // 预警弹框
      showWarnDialog: false,
      warnType: 0,
      warnList: [],
      warnSearchInfo: {pageNumber: 1, pageSize: 20, totalCount: 0},
    };
  },
  beforeRouteLeave(to, from, next) {
    // Util.cachePageData(this, to, from);
    next();
  },
  mounted() {
    this.roleType = Util.getRoleType();
    // 阶段名称走唯一性字典。字典与统计数据并行拉取，两边都到位后由 watch 触发渲染，
    // 避免先拿到统计数据、字典还没回来时把图表画成空名称。
    commonApi.queryUniqueDictionaryList(DICT_OPPORTUNITY_STAGE).then(list => {
      this.stageList = list || [];
      this.dictReady = true;
      this.renderIfReady();
    });
    this.loadOverview();
    this.loadWarnStat();
  },
  computed: {
    isSuperAdmin() {
      return this.roleType === 1;
    },
    /** 弹框标题：四档预警共用一个弹框，标题按档位取 */
    warnDialogTitle() {
      return (WARN_TYPE_TITLE[this.warnType] || "") + "的商机";
    },
    /** 当前弹框是不是跟进预警（1/2）—— 用来决定高亮「上次跟进」还是「预计成交」列 */
    isFollowWarn() {
      return this.warnType === WARN_TYPE.FOLLOW_7 || this.warnType === WARN_TYPE.FOLLOW_15;
    },
    /**
     * 阶段分布：以唯一性字典为准取全部阶段，没有商机的阶段补 0。
     * 后端只返回有数据的阶段，若直接用它画图，缺阶段时柱子会少几根。
     */
    chartStageRows() {
      return fillStageRows(this.stageList, this.overview.stageStats);
    },
  },
  unmounted() {
    [this.countChart, this.amountChart, this.trendChart].forEach(chart => chart && chart.dispose());
  },
  methods: {
    /** 概况与预警是同一次筛选下的两个口径，必须一起刷新，否则改了条件后两者的数字会对不上 */
    reloadAll() {
      this.loadOverview();
      this.loadWarnStat();
    },
    async loadOverview() {
      this.loading = true;
      try {
        const params = {};
        Object.keys(this.query).forEach(k => {
          const v = this.query[k];
          if (v === "" || v === null || v === undefined) {
            return;
          }
          params[k] = v;
        });
        this.overview = await opportunityOverviewApi.overview(params) || {};
        this.dataReady = true;
        this.$nextTick(() => this.renderIfReady());
      } finally {
        this.loading = false;
      }
    },
    /** 四档预警数量。与概况同一套筛选项，切换筛选后要重新拉，否则数字与上方 KPI 不是一个口径 */
    async loadWarnStat() {
      const params = {};
      Object.keys(this.query).forEach(k => {
        const v = this.query[k];
        if (v === "" || v === null || v === undefined) {
          return;
        }
        params[k] = v;
      });
      this.warnStat = await opportunityOverviewApi.warnStat(params) || {};
    },
    /** 点预警卡片：记录档位、回到第 1 页、拉列表 */
    openWarnDialog(warnType) {
      this.warnType = warnType;
      this.warnSearchInfo.pageNumber = 1;
      this.showWarnDialog = true;
      this.queryWarnList();
    },
    async queryWarnList() {
      const params = {
        warnType: this.warnType,
        pageNumber: this.warnSearchInfo.pageNumber,
        pageSize: this.warnSearchInfo.pageSize,
      };
      // 弹框里的列表同样要跟随页面上的筛选项，否则「筛选后点预警」看到的还是全部
      Object.keys(this.query).forEach(k => {
        const v = this.query[k];
        if (v === "" || v === null || v === undefined) {
          return;
        }
        params[k] = v;
      });
      const {records, totalCount} = await opportunityOverviewApi.queryWarnList(params);
      this.warnList = records || [];
      this.warnSearchInfo.totalCount = totalCount;
    },
    /** 日期只展示到天（后端返回带时分秒） */
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
    stageName(stageValue) {
      return dictName(this.stageList, stageValue);
    },
    stageTagType,
    /** 字典与统计数据都到位才渲染图表 */
    renderIfReady() {
      if (this.dictReady && this.dataReady) {
        this.renderCharts();
      }
    },
    renderCharts() {
      // 阶段名与补 0 由 chartStageRows 统一处理，字典未就绪时不画
      const rows = this.chartStageRows;
      const names = rows.map(s => s.stageTitle);

      this.countChart = this.renderBar(this.countChart, this.$refs.countChart, names,
          rows.map(s => s.opportunityCount), "#1677ff");
      this.amountChart = this.renderBar(this.amountChart, this.$refs.amountChart, names,
          rows.map(s => s.opportunityAmount), "#13b8b8");
      this.trendChart = this.renderLine(this.trendChart, this.$refs.trendChart,
          this.overview.trendMonthList || [], this.overview.trendAmountList || []);
    },
    /** 柱状图，图表实例复用，避免重复 init */
    renderBar(chart, dom, xNames, data, color) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true, axisPointer: {type: "shadow"}},
        grid: {left: "0", right: "0", bottom: "3%", top: "10%", containLabel: true},
        xAxis: {type: "category", data: xNames, axisTick: {alignWithLabel: true}, axisLabel: {color: "#909399"}},
        yAxis: {type: "value", splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
        series: [{type: "bar", barWidth: "40%", itemStyle: {color: color}, data: data}],
      });
      return chart;
    },
    /** 折线图 */
    renderLine(chart, dom, xNames, data) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true},
        grid: {left: "0", right: "0", bottom: "3%", top: "10%", containLabel: true},
        xAxis: {type: "category", boundaryGap: false, data: xNames, axisLabel: {color: "#909399"}},
        yAxis: {type: "value", splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
        series: [{type: "line", smooth: true, itemStyle: {color: "#1677ff"}, data: data}],
      });
      return chart;
    },
    money(value) {
      if (value === null || value === undefined) {
        return "0.00";
      }
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
  },
};
</script>

<style lang="scss" scoped>
/*
  预警条：刻意做成窄条 + 药丸标签，与上方白底大卡片区分开 ——
  那些是「看数」的，这些是「点进去干活」的。
*/
.warn-panel {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
  .warn-row {
    display: flex;
    align-items: center;
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    background: #fff;
    padding: 10px 16px;

  }

  .warn-title {
    flex: none;
    width: 72px;
    font-size: 13px;
    font-weight: 700;
    color: #606266;
  }

  .warn-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .warn-tag {
    display: inline-flex;
    align-items: center;
    padding: 3px 12px;
    border-radius: 12px;
    font-size: 12px;
    cursor: pointer;
    user-select: none;
    background: #f5f7fa;
    color: #606266;
    transition: filter 0.2s;

    &:hover {
      filter: brightness(0.95);
    }

    b {
      margin-left: 6px;
      font-size: 15px;
    }

    // 颜色对齐「紧急程度」：越红越急
    &.warn-tag-danger {
      color: #c0392b;
      background: #fdecea;
    }

    &.warn-tag-warn {
      color: #e67e22;
      background: #fef3e7;
    }

    &.warn-tag-info {
      color: #2980b9;
      background: #eaf3fb;
    }

    &.warn-tag-primary {
      color: #4b3fbb;
      background: #eeecfb;
    }
  }
}

// 弹框里标出「当前这一档看的就是这个日期」，另一列保持普通颜色
.warn-cell {
  color: #c0392b;
  font-weight: 700;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .label {
      font-size: 12px;
      color: #909399;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      height: 34px;
      line-height: 34px;
      margin: 4px 0;

      &.money {
        font-size: 20px;
      }


      .unit {
        font-size: 14px;
        font-weight: 400;
        margin-left: 2px;
      }
    }

    .sub {
      font-size: 12px;
      color: #909399;
    }
  }
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.chart-card {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;
  }

  .chart-div {
    height: 280px;
    width: 100%;
  }
}
</style>
